import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { isNativePlatform } from '@/lib/platform';
import { popBackHandler } from '@/lib/backHandlerRegistry';
import { debug, error } from '@/utils/logger';
import { getBackFallbackPath, getFromStatePath } from '@/utils/backNavigation';

/**
 * Legacy DOM-scanning overlay closer. Kept as a fallback for overlays that
 * haven't been migrated to `useBackHandler` from `@/lib/backHandlerRegistry`
 * yet. The new path is preferred: it's deterministic, refactor-safe, and
 * doesn't depend on CSS class names. Each overlay should register a
 * back-handler at the source (next to its `isOpen` state) and let the
 * registry pop it in LIFO order on hardware back.
 *
 * TODO: once every overlay calls useBackHandler, delete this function.
 */
const OVERLAY_SELECTORS = [
  '.mobile-tab-menu-overlay',
  '.nav-overlay',
  '.community-modal-overlay',
  '.modal-overlay',
] as const;

function closeTopOverlayIfPresent(): boolean {
  for (const selector of OVERLAY_SELECTORS) {
    const overlay = document.querySelector(selector) as HTMLElement | null;
    if (overlay) {
      overlay.click();
      return true;
    }
  }

  const dialog = document.querySelector('[role="dialog"], .mobile-profile-sheet, .mobile-tab-menu');
  if (dialog) {
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    const closeButton = document.querySelector(
      '[aria-label="Close"], [aria-label="Close menu"], [aria-label="Close create menu"]'
    ) as HTMLElement | null;
    closeButton?.click();
    return true;
  }

  return false;
}

export function useNativeBackButton(): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isNativePlatform()) return;

    const listenerPromise = App.addListener('backButton', ({ canGoBack }) => {
      // Preferred path: deterministic LIFO registry. Each overlay registers
      // its close function via `useBackHandler`; the topmost (most recently
      // opened) handler is popped and invoked. No DOM coupling.
      if (popBackHandler()) {
        debug('[useNativeBackButton] Closed top overlay via back-handler registry');
        return;
      }

      // Legacy fallback for overlays not yet migrated to useBackHandler.
      // Scans the DOM for known overlay class names and synthesises a click
      // or Escape keypress. Brittle — see closeTopOverlayIfPresent docstring.
      if (closeTopOverlayIfPresent()) {
        debug('[useNativeBackButton] Closed overlay via legacy DOM scan');
        return;
      }

      const currentPathWithQuery = `${location.pathname}${location.search}`;
      const fromPath = getFromStatePath(location.state, currentPathWithQuery);
      if (fromPath) {
        navigate(fromPath);
        return;
      }

      if (window.history.length > 1 && canGoBack) {
        navigate(-1);
        return;
      }

      const fallback = getBackFallbackPath(location.pathname);
      if (fallback !== location.pathname) {
        navigate(fallback, { replace: true });
        return;
      }

      App.exitApp().catch((e) => {
        error('[useNativeBackButton] Failed to exit app', e);
      });
    });

    return () => {
      void listenerPromise.then((handle) => handle.remove()).catch((e) => {
        error('[useNativeBackButton] Failed to remove backButton listener', e);
      });
    };
  }, [navigate, location.pathname, location.search, location.state]);
}
